/**
 * The `{{variable}}` grammar shared by every T&C template surface.
 *
 * Lifted verbatim out of sm4rt-web's `utils/quoteTemplateRenderer.ts`, which
 * kept the grammar and the quote-specific value map in one file. Only the
 * grammar moves here; each caller still builds its own value map, because what
 * a variable *means* is surface-specific and what it *does* is not.
 *
 * Grammar (deliberately small):
 *   - `{{path.to.value}}`      — interpolation against a pre-formatted map.
 *   - `{{#if path}}...{{/if}}` — single-branch conditional, kept iff the
 *                                resolved value is non-empty. No else/elsif.
 *   - Unknown placeholders render empty.
 *
 * ### This runs twice, in two languages
 *
 * sm4rt-public-api resolves the same grammar in Python: it is authoritative
 * about the terms body a customer accepted, so it cannot take the browser's
 * word for the resolved text. Two implementations of one grammar drift, so
 * the case table in `fixtures/terms-template-rendering.json` is the contract
 * between them — a Jest test here and a pytest there both read it. Change the
 * behaviour and you change the fixture; change the fixture and the other side
 * fails until it follows.
 *
 * Values are pre-formatted strings, never raw numbers or dates. That keeps
 * currency, hours and date formatting on the side that knows the locale, and
 * keeps this function a pure string transform.
 */

/** Pre-formatted variable values, keyed by the dot-path used in `{{ }}`. */
export type TemplateValues = Record<string, string>;

/** Matches `{{#if path}}…{{/if}}`, non-greedy so adjacent blocks stay apart. */
const CONDITIONAL_RE = /\{\{#if\s+([\w.]+)\s*\}\}([\s\S]*?)\{\{\/if\}\}/g;

/** Matches `{{ path }}`, tolerating whitespace inside the braces. */
const PLACEHOLDER_RE = /\{\{\s*([\w.]+)\s*\}\}/g;

/**
 * Substitute a template body against a value map.
 *
 * Empty / null / undefined `source` is a safe pass-through (`''` out).
 * Conditional blocks must be balanced; a mismatched `{{#if}}` / `{{/if}}`
 * falls through to the unknown-placeholder rule, leaving the offending block
 * in the output so the author can see it rather than losing their text.
 *
 * Ordering matters and is part of the contract: conditionals resolve first,
 * so a `{{var}}` inside a dropped block never renders.
 */
export function renderTemplateSource(
  source: string | null | undefined,
  values: TemplateValues,
): string {
  if (!source) return '';

  let result = source.replace(
    CONDITIONAL_RE,
    (_match, key: string, content: string) => {
      const v = values[key];
      return v && v.trim() !== '' ? content : '';
    },
  );

  result = result.replace(
    PLACEHOLDER_RE,
    (_match, key: string) => values[key] ?? '',
  );

  // Dropping a conditional leaves the blank lines that surrounded it. Collapse
  // them, or every optional clause leaves a visible hole in the terms.
  return result.replace(/\n{3,}/g, '\n\n').trim();
}

/**
 * Every distinct path referenced by a template body — interpolations and
 * conditionals alike.
 *
 * Used by the admin editors to flag typos. Deliberately not used to reject a
 * body: an unknown placeholder renders empty and a template may legitimately
 * be written against a variable an older deployment does not have yet, so the
 * editors warn and the renderer stays permissive.
 */
export function collectTemplatePaths(
  source: string | null | undefined,
): string[] {
  if (!source) return [];
  const seen = new Set<string>();

  for (const match of source.matchAll(CONDITIONAL_RE)) {
    seen.add(match[1]);
  }
  // Run the placeholder sweep over the ORIGINAL source, not the conditional
  // remainder: a `{{var}}` inside an `{{#if}}` block is still a reference the
  // author meant, and a typo inside a conditional is exactly the one that
  // hides at runtime.
  for (const match of source.matchAll(PLACEHOLDER_RE)) {
    seen.add(match[1]);
  }

  return [...seen];
}

/**
 * Paths a body references that no variable in `known` provides.
 *
 * `known` is the registry for the surface being edited — `QUOTE_TEMPLATE_VARIABLES`
 * or `CHECKIN_TEMPLATE_VARIABLES`, mapped to their `path`.
 */
export function findUnknownTemplatePaths(
  source: string | null | undefined,
  known: readonly string[],
): string[] {
  const allowed = new Set(known);
  return collectTemplatePaths(source).filter((path) => !allowed.has(path));
}
